<template>
  <div
    :class="[
      'owc-media-slider-item',
      { 'owc-media-slider-item__active': isActive },
      { 'owc-media-slider-item__dark': darkMode },
      { 'owc-media-slider-item__isEditMode': isEditMode },
    ]"
    :data-theme="brand"
    :aria-disabled="!isActive"
  >
    <ValidationBoxWrapper v-if="isEditMode">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <div class="owc-media-slider-item__content">
      <a
        v-if="isImage(type) && ctaData"
        :href="ctaData.url"
        :target="ctaData.target"
        @click="handleTracking('image', ctaData ? ctaData.url : '')"
        :aria-label="ctaData.aria"
        data-track="false"
      >
        <SlotContainer refName="slot-media-slider-item">
          <slot></slot>
        </SlotContainer>
      </a>
      <SlotContainer v-else refName="slot-media-slider-item">
        <slot></slot>
      </SlotContainer>
    </div>
    <p ref="title" v-if="title" class="owc-media-slider-item__title wb-type-copy-strong" v-html="title"></p>
    <p v-if="description" class="owc-media-slider-item__text wb-type-copy" v-html="description"></p>
    <a
      v-if="ctaData"
      :href="ctaData.url"
      :target="ctaData.target"
      :aria-label="ctaData.aria"
      :class="[
        'wb-button wb-button--secondary',
        'wb-button--medium',
        'owc-media-slider-item__cta',
        { 'wb-button--theme-dark': darkMode },
        { 'owc-media-slider-item__cta-visible': isActive },
      ]"
      @click="handleTracking('cta', ctaData ? ctaData.url : '')"
      data-track="false"
    >
      {{ ctaData.label }}
    </a>
  </div>
</template>

<script lang="ts">
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import GenericImage from '@owc-packages/GenericImage/src/GenericImage.vue';
import { MediaSliderItemComponent } from './MediaSliderItem';
import { ItemType } from './MediaSliderItem.enums';
import VideoPlayer from '@owc-packages/VideoPlayer/src/VideoPlayer.vue';
import { TrackingInstance } from '@owc-packages/Tracking/src/Tracking';
import initializeTracking, { formatTrackingString } from '@owc-packages/Tracking/src/TrackingUtils';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import { setBrandTheme } from '@owc-utils/Theming/theming';

let trackingInstance: TrackingInstance | null = null;

export default (ComponentInitializeDispatcher as MediaSliderItemComponent).extend({
  mixins: [OwcDataComponent],
  name: 'owc-media-slider-item',
  components: {
    GenericImage,
    VideoPlayer,
    SlotContainer,
    ValidationBoxWrapper,
  },
  async mounted() {
    trackingInstance = await initializeTracking();
  },
  methods: {
    isImage(item) {
      return item === ItemType.image;
    },
    handleTracking(labelPrefix, url) {
      if (trackingInstance) {
        const title = this.$refs.title.textContent || '';
        trackingInstance.customTrackingObjectUpdate(
          {
            label: `${labelPrefix}.${formatTrackingString(title)}`,
            category: 'link',
            URL: url,
          },
          'click',
          this.$el,
        );
      }
    },
  },
  computed: {
    title() {
      return this.componentData?.title;
    },
    description() {
      return this.componentData?.description;
    },
    ctaData() {
      return this.componentData?.buttonData;
    },
    type() {
      return this.componentData?.type;
    },
    isActive() {
      return this.componentData?.isActive;
    },
    darkMode() {
      return this.componentData?.darkMode;
    },
    brand() {
      return setBrandTheme();
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/experiments';
@import '@owc-styles/utils/rte';
@import '@owc-styles/utils/theming';

.owc-media-slider-item {
  opacity: 0.2;

  pointer-events: none;

  &__dark {
    color: var(--wb-white);
  }
  &__active {
    opacity: 1;

    pointer-events: all;
  }
  &__content {
    padding-bottom: var(--wb-spacing-xs);
  }
  &__title {
    margin-bottom: var(--wb-spacing-xxs);

    word-break: break-word;

    @include ellipsis-multiline(1);
  }
  &__text {
    margin-bottom: var(--wb-spacing-xs);

    word-break: break-word;

    @include breakpoint-to(mq2) {
      @include ellipsis-multiline(5);
    }
    @include breakpoint-between(mq2, mq5) {
      @include ellipsis-multiline(4);
    }
    @include breakpoint-from(mq5) {
      @include ellipsis-multiline(3);
    }
  }
  &__cta {
    width: 100%;

    opacity: 0;

    @include breakpoint-from(mq3) {
      width: auto;
    }

    &-visible {
      opacity: 1;
    }
  }
  &.owc-active {
    opacity: 1;

    pointer-events: all;

    .owc-media-slider-item__cta {
      opacity: 1;
    }
  }
}
</style>

<style lang="scss">
.owc-media-slider-item__isEditMode {
  padding: 1.25em;

  opacity: 1;

  .owc-media-slider-item__cta {
    opacity: 1;
  }
}
</style>
